/**
 * useInstallPrompt.js — добавление PWA на экран телефона/десктопа.
 *
 * Три исхода, которые видит кнопка «Добавить на экран»:
 *   • canInstall  — Chrome/Android/десктоп поймали `beforeinstallprompt`,
 *     можно звать promptInstall() и получить нативный диалог браузера.
 *   • isIOS && !isStandalone — Safari не даёт программного пути вообще
 *     (нет такого API), показываем модалку-инструкцию «Поделиться → На
 *     экран «Домой»».
 *   • ни то ни другое — браузер либо не поддерживает установку (Firefox
 *     desktop и т.п.), либо событие ещё не пришло. Кнопку в этом случае
 *     решает прятать или дизейблить сам вызывающий компонент.
 *
 * isStandalone — уже запущено как установленное приложение, кнопка не нужна
 * ни на одной платформе.
 *
 * `beforeinstallprompt` — одноразовое событие браузера: если страница его не
 * поймала подпиской ДО того, как браузер решил его выстрелить, оно потеряно
 * до следующей перезагрузки. Поэтому слушатель вешаем в App.js на всё дерево
 * (см. InstallPromptProvider), а не только на странице Настроек — иначе кнопка
 * там почти всегда просто не работала бы.
 */

import { useState, useEffect, useCallback, createContext, useContext } from 'react';
import { isIosLike } from '../utils/fileShare';

const InstallPromptContext = createContext(null);

function detectStandalone() {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia?.('(display-mode: standalone)')?.matches ||
    window.navigator?.standalone === true // iOS Safari
  );
}

/** Ставится один раз в App.js — держит deferredPrompt, пока его не заберут. */
export function useInstallPromptState() {
  const [deferredEvent, setDeferredEvent] = useState(null);
  const [installed, setInstalled] = useState(detectStandalone);

  useEffect(() => {
    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredEvent(e);
    };
    const onInstalled = () => {
      setInstalled(true);
      setDeferredEvent(null);
    };
    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const promptInstall = useCallback(async () => {
    if (!deferredEvent) return { ok: false, reason: 'unavailable' };
    deferredEvent.prompt();
    const choice = await deferredEvent.userChoice;
    setDeferredEvent(null);
    return { ok: choice.outcome === 'accepted', outcome: choice.outcome };
  }, [deferredEvent]);

  return {
    canInstall: !!deferredEvent,
    installed,
    isIOS: isIosLike(),
    promptInstall,
  };
}

export const InstallPromptProvider = InstallPromptContext.Provider;

/** Использование в компонентах — карточка в Настройках, шаг чек-листа и т.п. */
export function useInstallPrompt() {
  const ctx = useContext(InstallPromptContext);
  if (!ctx) {
    // Провайдер не смонтирован (напр. изолированный тест) — деградируем в
    // безопасный «ничего не умеем», а не роняем компонент.
    return { canInstall: false, installed: detectStandalone(), isIOS: isIosLike(), promptInstall: async () => ({ ok: false, reason: 'no_provider' }) };
  }
  return ctx;
}
